import { Box, Flex, Text } from "@chakra-ui/react";
import { Col, Modal, Row } from "antd";
import { CloseModalButton } from "components/Button/CloseModalButton";
import FooterModal from "components/Button/FooterModal";
import DateRangePicker from "components/Input/DateRangePicker";
import Toolbar from "components/Toolbar/index";
import { RelatorioComposicaoContasQueryParams } from "core/features/relatorios/conta-com-composicoes/typings/index";
import { useState, useEffect } from "react";
import { defaultFiltroRelatorioComposicaoContas } from "./hooks/useFiltroRelatorioComposicaoContas";

import SelectContaBancaria from "ui/components/Input/SelectContaBancaria";
import SelectContaCategoria from "ui/components/Input/SelectContaCategoria";
import SelectUnidade from "ui/components/Input/SelectUnidade";
import SelectTipoData from "components/Input/SelectTipoData";
import { OperacaoEnum } from "resources/enums/conta";
import SelectCentroDeCusto from "../../Input/SelectCentroDeCusto/index";
import SelectUsuarioResponsavel from "components/Input/SelectUsuarioResponsavel";
import { Tooltip } from "antd";
import { AiOutlineQuestionCircle } from "react-icons/ai";

interface ModalFiltrosRelatorioComposicaoContasProps {
  onClose: () => void;
  onReset: () => void;
  filtro: RelatorioComposicaoContasQueryParams;
  visible: boolean;
  onChangeFiltro: (
    filtro: Partial<RelatorioComposicaoContasQueryParams>
  ) => void;
  tenantId: string;
  operacao: OperacaoEnum;
}

export default function ModalFiltrosRelatorioComposicaoContas({
  onClose,
  visible,
  onChangeFiltro,
  onReset,
  filtro,
  tenantId,
  operacao,
}: ModalFiltrosRelatorioComposicaoContasProps) {
  const [localFiltro, setLocalFiltro] =
    useState<Partial<RelatorioComposicaoContasQueryParams>>(filtro);

  const numeroFiltroSelecionados =
    (localFiltro.contasCategoriasIds?.length ? 1 : 0) +
    (localFiltro.dataInicio || localFiltro.dataFim ? 1 : 0) +
    (localFiltro.tipoData ? 1 : 0) +
    (localFiltro.unidadesIds?.length ? 1 : 0) +
    (localFiltro.centrosDeCustoIds?.length ? 1 : 0) +
    (localFiltro.contasBancariasIds?.length ? 1 : 0) +
    (localFiltro.usuarioOperadorIds?.length ? 1 : 0);

  useEffect(() => {
    if (!visible) {
      setLocalFiltro(filtro);
    }
  }, [visible, filtro]);

  return (
    <Modal
      centered
      destroyOnClose
      closable={false}
      width="700px"
      visible={visible}
      className="antd-custom-header"
      title={
        <Toolbar title="Filtros avançados" onClose={onClose} border="0px" />
      }
      footer={
        <Flex
          justifyContent="end"
          align="center"
          gap="8px"
          alignContent="center"
          alignItems="center"
        >
          <CloseModalButton
            closeModalLabel="Fechar"
            close={onClose}
            mt="32px"
          />
          <FooterModal
            isDisabled={false}
            onSubmit={() => {
              onChangeFiltro(localFiltro);
              onClose();
            }}
            onClose={() => {
              onReset();
              setLocalFiltro(defaultFiltroRelatorioComposicaoContas);
              onClose();
            }}
            showRegisterAnother={false}
            primaryButtonText={`Filtrar ${typeof numeroFiltroSelecionados === "number" &&
              numeroFiltroSelecionados > 0
              ? ` (${numeroFiltroSelecionados})`
              : ""
              }`}
            buttonCancelarText="Limpar filtros"
            isLoadingSave={false}
          />
        </Flex>
      }
    >
      <Box as={Row} gutter={16} mb={4}>
        <Col md={12}>
          <Text mb="2px" fontSize="13px">
            Tipo de data
          </Text>
          <SelectTipoData
            bg="white"
            style={{ border: "1px solid #ccc", borderRadius: 6 }}
            value={localFiltro.tipoData}
            onChange={(v) =>
              setLocalFiltro((state) => ({
                ...state,
                tipoData: v,
              }))
            }
            operacao={operacao}
          />
        </Col>

        <Col md={12}>
          <Text mb="2px" fontSize="13px">
            Período
          </Text>
          <DateRangePicker
            onChange={(dataInicio, dataFim) => {
              setLocalFiltro((state) => ({
                ...state,
                dataInicio,
                dataFim,
              }));
            }}
            value={{
              dataInicio: localFiltro.dataInicio,
              dataFim: localFiltro.dataFim,
            }}
          />
        </Col>
      </Box>

      <Box mb={4}>
        <Text mb="2px" fontSize="13px">
          Conta Categoria
        </Text>
        <SelectContaCategoria
          multiple
          value={localFiltro.contasCategoriasIds}
          placeholder="Todas as contas categorias"
          onChange={(v) => {
            setLocalFiltro((state) => ({
              ...state,
              contasCategoriasIds: v.length ? v : [],
            }));
          }}
        />
      </Box>

      <Box mb={4}>
        <Text mb="2px" fontSize="13px">
          Conta Bancária
        </Text>
        <SelectContaBancaria
          placeholder="Todas as contas bancárias"
          value={localFiltro.contasBancariasIds}
          mode="multiple"
          onChange={(contasBancariasIds) => {
            setLocalFiltro((state) => ({ ...state, contasBancariasIds }));
          }}
        />
      </Box>

      <Box mb={4}>
        <Text mb="2px" fontSize="13px">
          Centro de Custo
        </Text>
        <SelectCentroDeCusto
          allowClear
          placeholder="Todos os centros de custo"
          value={localFiltro.centrosDeCustoIds}
          mode="multiple"
          onChange={(centrosDeCustoIds) => {
            setLocalFiltro((state) => ({ ...state, centrosDeCustoIds }));
          }}
        />
      </Box>

      <Box mb={4}>
        <Text mb="2px" fontSize="13px">
          Unidades
        </Text>
        <SelectUnidade
          mode="multiple"
          tenantId={tenantId}
          onChange={(unidadesIds) => {
            setLocalFiltro((state) => ({
              ...state,
              unidadesIds,
            }));
          }}
          value={localFiltro.unidadesIds}
        />
      </Box>

      <Box mb={4}>
        <Flex gap={2} alignItems="center">
          <Text mb="2px" fontSize="13px">
            Usuário operador do acordo
          </Text>
          <Box as="span" mt={-2}>
            <Tooltip title="Isso irá filtrar apenas receitas originadas por acordos.">
              <AiOutlineQuestionCircle cursor={"pointer"} size={15} />
            </Tooltip>
          </Box>
        </Flex>
        <SelectUsuarioResponsavel
          mode="multiple"
          onChange={(usuarioOperadorIds) => {
            setLocalFiltro((state) => ({
              ...state,
              usuarioOperadorIds,
            }));
          }}
          value={localFiltro.usuarioOperadorIds}
        />
      </Box>
    </Modal>
  );
}
